import { createEvent, createStore, sample } from 'effector';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { $notes, NotesState } from '@nx-frontend/shared/store/notes';
import { BreadcrumbsItem, MenuType } from '@nx-frontend/shared/types';
import { MenuItemType } from '@nx-frontend/shared/types';
import { Workspace } from '@nx-frontend/shared/types';
import { MenusState } from '@nx-frontend/shared/types';

export interface BreadcrumbsState {
  path: BreadcrumbsItem[];
  prevPath: string;
  allPagesVisited: boolean;
  tagVisited?: string;
}

export interface buildBreadcrumbsEventPayload {
  stores: {
    notes: NotesState;
    menuState: MenusState;
    currentWorkspace: Workspace;
  };
  itemId: string;
}

export const initialBreadcrumbsState = {
  path: [],
  prevPath: '',
  allPagesVisited: false,
};

export const $breadcrumbs = createStore<BreadcrumbsState>(initialBreadcrumbsState);

export const createBreadcrumbsEvent = createEvent<string>();
export const buildBreadcrumbsEvent = createEvent<buildBreadcrumbsEventPayload>();
export const setBreadcrumbsAllPageVisitedEvent = createEvent<boolean>();
export const setBreadcrumbsTagVisitedEvent = createEvent<string | undefined>();
export const clearBreadcrumbsSpecialPagesVisitedEvent = createEvent();

sample({
  source: [$notes, $menuState, $currentWorkspace],
  clock: createBreadcrumbsEvent,
  fn: (stores, itemId) => {
    return {
      stores: {
        notes: stores[0] as NotesState,
        menuState: stores[1] as MenusState,
        currentWorkspace: stores[2] as Workspace,
      },
      itemId,
    };
  },
  target: [buildBreadcrumbsEvent],
});

$breadcrumbs.on(buildBreadcrumbsEvent, (_, params) => {
  const { stores, itemId } = params;
  const { notes, menuState, currentWorkspace } = stores;

  const menuStateEntities = menuState[MenuType.WEBNOTES_MENU]?.entities || {};

  const noteInNotesState = notes.workspaces[currentWorkspace.globalId]?.entities[itemId];
  const noteInMenuState = menuStateEntities[`${MenuItemType.NOTE}#${itemId}`];
  const folderInMenuState = menuStateEntities[`${MenuItemType.NOTES_FOLDER}#${itemId}`];

  const itemParentId: string =
    noteInNotesState?.parentId ||
    noteInMenuState?.parentId?.replace(MenuItemType.NOTES_FOLDER + '#', '') ||
    folderInMenuState?.parentId?.replace(MenuItemType.NOTES_FOLDER + '#', '') ||
    '';

  const path: BreadcrumbsItem[] = [
    {
      id: noteInNotesState?.globalId || noteInMenuState?.id.split('#')[1] || folderInMenuState?.id.split('#')[1] || '',
      name: noteInNotesState?.title || noteInMenuState?.name || folderInMenuState?.name || '',
      url: '',
    },
  ];

  const findParents = (id: string, menuState: string) => {
    const folder = notes.workspaces[currentWorkspace.globalId]?.entities[id];

    if (!folder) return;

    if (menuState !== 'folder') {
      path.unshift({
        name: folder.title,
        id: folder.globalId,
        url: `/space/${currentWorkspace.globalId}/folder/${folder.globalId}`,
      });
    }

    if (menuState === 'folder' && !path.some((folderInPath) => folderInPath.id === folder.globalId)) {
      path.unshift({
        name: folder.title,
        id: folder.globalId,
        url: `/space/${currentWorkspace.globalId}/folder/${folder.globalId}`,
      });
    }

    const parentFolderId = folder.parentId;

    if (parentFolderId) {
      const parentFolder = notes.workspaces[currentWorkspace.globalId]?.entities[parentFolderId];

      if (parentFolder) {
        findParents(parentFolderId, menuState);
      } else {
        path.unshift({
          name: 'Folders',
          nameLocKey: 'webnotes.webnotes-folders',
          id: parentFolderId,

          url: `/space/${currentWorkspace.globalId}/folder/root`,
        });
      }
    } else {
      path.unshift({
        name: 'Folders',
        nameLocKey: 'webnotes.webnotes-folders',
        id: parentFolderId,
        url: `/space/${currentWorkspace.globalId}/folder/root`,
      });
    }
  };

  if (folderInMenuState) {
    findParents(itemId, 'folder');
  }
  if (noteInMenuState) {
    findParents(itemParentId, 'note');
  }

  let prevPath: string = path.length >= 2 ? path[path.length - 2].url : '';

  if (_.allPagesVisited === true) {
    prevPath = `/space/${currentWorkspace.globalId}/all`;
  }
  if (_.tagVisited) {
    prevPath = `/space/${currentWorkspace.globalId}/tag/${encodeURIComponent(_.tagVisited)}`;
  }
  return {
    ..._,
    path,
    prevPath,
  };
});

$breadcrumbs.on(setBreadcrumbsAllPageVisitedEvent, (_, allPagesVisited) => {
  return {
    ..._,
    allPagesVisited,
  };
});

$breadcrumbs.on(setBreadcrumbsTagVisitedEvent, (_, tagVisited) => {
  return {
    ..._,
    tagVisited,
  };
});

clearBreadcrumbsSpecialPagesVisitedEvent.watch(() => {
  setBreadcrumbsAllPageVisitedEvent(false);
  setBreadcrumbsTagVisitedEvent(undefined);
});

/**
 * Set breadcrumbs event
 */
export const setBreadcrumbsEvent = createEvent<BreadcrumbsState>();

$breadcrumbs.on(setBreadcrumbsEvent, (_, breadcrumbs) => {
  return breadcrumbs;
});
